import { HTMLAttributes } from "react";
import { UUID } from "@bitory-gmbh/nidavellir";
import { GSMServiceKind } from "@/services/backend/services/types/kinds";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import { ReferenceNumber } from "@/services/backend/documents/components/reference-number";
import { DocumentState } from "@/services/backend/documents/types/state";
import { DocumentStateBadge } from "@/shared/bsm/core/document/components/ui/document-state-badge";
import { Skeleton } from "@/shared/components/ui/skeleton";
import {
  is404,
  parseRTKQueryError,
} from "@/shared/components/errors/parse-r-t-k-query-error";
import t from "@/lang/lang";
import { useHtzOfferShowQuery } from "@/services/backend/htz/offers/api/offer-api";
import { useHtzConfirmationShowQuery } from "@/services/backend/htz/confirmations/api/confirmation-api";
import { useHtzOrderShowQuery } from "@/services/backend/htz/orders/api/order-api";
import { useHtzWorkOrderShowQuery } from "@/services/backend/htz/work-order/api/work-order-api";
import { useHtzReportShowQuery } from "@/services/backend/htz/report/api/report-api";
import { useHtzSummaryShowQuery } from "@/services/backend/htz/summary/api/summary-api";
import { useRltOfferShowQuery } from "@/services/backend/rlt/offers/api/offer-api";
import { useRltConfirmationShowQuery } from "@/services/backend/rlt/confirmations/api/confirmation-api";
import { useRltContractShowQuery } from "@/services/backend/rlt/contracts/api/contract-api";
import { useRltOrderShowQuery } from "@/services/backend/rlt/orders/api/order-api";
import { useRltWorkOrderShowQuery } from "@/services/backend/rlt/work-orders/api/work-order-api";
import { useRltCertificateShowQuery } from "@/services/backend/rlt/certificates/api/certificate-api";
import { useRltDocumentationShowQuery } from "@/services/backend/rlt/work-orders/api/documentation-api";
import { useRltLMRAShowQuery } from "@/services/backend/rlt/work-orders/api/lmra-api";
import { useRltCalculationShowQuery } from "@/services/backend/rlt/calculations/api/calculation-api";
import { useRltTreatmentShowQuery } from "@/services/backend/rlt/treatments/api/treatment-api";
import { useVbsOfferShowQuery } from "@/services/backend/vbs/offers/api/offer-api";
import { useVbsConfirmationShowQuery } from "@/services/backend/vbs/confirmations/api/confirmation-api";
import { useVbsContractShowQuery } from "@/services/backend/vbs/contracts/api/contract-api";
import { useVbsOrderShowQuery } from "@/services/backend/vbs/orders/api/order-api";
import { useVbsWorkOrderShowQuery } from "@/services/backend/vbs/work-orders/api/work-order-api";
import { useVbsCertificateShowQuery } from "@/services/backend/vbs/certificates/api/certificate-api";
import { useVbsDocumentationShowQuery } from "@/services/backend/vbs/work-orders/api/documentation-api";
import { useVbsLMRAShowQuery } from "@/services/backend/vbs/work-orders/api/lmra-api";
import { useVbsCalculationShowQuery } from "@/services/backend/vbs/calculations/api/calculation-api";
import { useVbsTreatmentShowQuery } from "@/services/backend/vbs/treatments/api/treatment-api";

export interface DocumentReferenceNumberByIdProps extends HTMLAttributes<HTMLSpanElement> {
  serviceKind: string;
  documentKind: string;
  documentId: UUID;
  /**
   * showState renders the [DocumentState] of the document next to its
   * number. Documents outside HTZ, RLT and VBS have no state, nothing is
   * shown for them.
   */
  showState?: boolean;
}

/**
 * DocumentReferenceNumberById displays the reference number of a document
 * that is only known by its id and kind. It loads the document through the
 * show query of the owning subdomain.
 *
 * HTZ, RLT and VBS documents carry their reference number themselves. For
 * all other documents (e.g. QPM, GFS) the number is read from the numbers
 * subdomain.
 */
export function DocumentReferenceNumberById({
  serviceKind,
  documentKind,
  documentId,
  showState = false,
  ...props
}: DocumentReferenceNumberByIdProps) {
  const showQuery = showQueryHook(serviceKind, documentKind);
  if (!showQuery) {
    return <DocumentNumberById documentId={documentId} {...props} />;
  }

  return (
    <ReferenceNumberByQuery
      // remount when the kinds change, the hook must not change for a mount
      key={`${serviceKind}-${documentKind}`}
      useShowQuery={showQuery}
      documentId={documentId}
      showState={showState}
      {...props}
    />
  );
}

type ShowQueryHook = (request: { id: string }) => {
  data?: { referenceNumber: string; state?: DocumentState };
  isLoading: boolean;
  error?: unknown;
};

function showQueryHook(
  serviceKind: string,
  documentKind: string,
): ShowQueryHook | null {
  return showQueryHooks[serviceKind]?.[documentKind] ?? null;
}

const showQueryHooks: Record<string, Record<string, ShowQueryHook>> = {
  [GSMServiceKind.HTZ]: {
    [GSMDocumentKind.Offer]: useHtzOfferShowQuery,
    [GSMDocumentKind.Confirmation]: useHtzConfirmationShowQuery,
    [GSMDocumentKind.Order]: useHtzOrderShowQuery,
    [GSMDocumentKind.WorkOrder]: useHtzWorkOrderShowQuery,
    [GSMDocumentKind.Report]: useHtzReportShowQuery,
    summaryreport: useHtzSummaryShowQuery,
  },
  [GSMServiceKind.RLT]: {
    [GSMDocumentKind.Offer]: useRltOfferShowQuery,
    [GSMDocumentKind.Confirmation]: useRltConfirmationShowQuery,
    [GSMDocumentKind.Contract]: useRltContractShowQuery,
    [GSMDocumentKind.Order]: useRltOrderShowQuery,
    [GSMDocumentKind.WorkOrder]: useRltWorkOrderShowQuery,
    [GSMDocumentKind.Certificate]: useRltCertificateShowQuery,
    [GSMDocumentKind.Documentation]: useRltDocumentationShowQuery,
    [GSMDocumentKind.LastMinuteRiskAnalysis]: useRltLMRAShowQuery,
    [GSMDocumentKind.Calculation]: useRltCalculationShowQuery,
    [GSMDocumentKind.Treatment]: useRltTreatmentShowQuery,
  },
  [GSMServiceKind.VBS]: {
    [GSMDocumentKind.Offer]: useVbsOfferShowQuery,
    [GSMDocumentKind.Confirmation]: useVbsConfirmationShowQuery,
    [GSMDocumentKind.Contract]: useVbsContractShowQuery,
    [GSMDocumentKind.Order]: useVbsOrderShowQuery,
    [GSMDocumentKind.WorkOrder]: useVbsWorkOrderShowQuery,
    [GSMDocumentKind.Certificate]: useVbsCertificateShowQuery,
    [GSMDocumentKind.Documentation]: useVbsDocumentationShowQuery,
    [GSMDocumentKind.LastMinuteRiskAnalysis]: useVbsLMRAShowQuery,
    [GSMDocumentKind.Calculation]: useVbsCalculationShowQuery,
    [GSMDocumentKind.Treatment]: useVbsTreatmentShowQuery,
  },
};

function ReferenceNumberByQuery({
  useShowQuery,
  documentId,
  showState,
  ...props
}: HTMLAttributes<HTMLSpanElement> & {
  useShowQuery: ShowQueryHook;
  documentId: UUID;
  showState: boolean;
}) {
  const { data, isLoading, error } = useShowQuery({ id: documentId });

  if (isLoading) {
    return <Skeleton className="h-2 w-52" />;
  }

  if (error) {
    if (is404(error)) {
      return <ReferenceNumber referenceNumber="" {...props} />;
    }
    const err = parseRTKQueryError(
      error as Parameters<typeof parseRTKQueryError>[0],
    );
    return (
      <span className="whitespace-nowrap">{err?.title ?? t("Fehler")}</span>
    );
  }

  if (!showState || !data?.state) {
    return (
      <ReferenceNumber referenceNumber={data?.referenceNumber} {...props} />
    );
  }

  return (
    <span className="inline-flex items-center gap-2">
      <ReferenceNumber referenceNumber={data.referenceNumber} {...props} />
      <DocumentStateBadge document={{ state: data.state }} />
    </span>
  );
}
